import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { fetchBlogPosts } from "@/lib/api";
import NewsletterSignup from "@/components/NewsletterSignup";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBlogPosts()
      .then((data) => setPosts(data))
      .catch((e) => setError(e?.message || "Failed to load posts"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-page">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 lg:pt-24 pb-10">
        <p className="overline text-terracotta">Dr. Delmar's Blog</p>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-ink mt-4 max-w-3xl">
          Honest advice from a vet who's been there.
        </h1>
        <p className="mt-6 text-lg text-ink-muted max-w-2xl leading-relaxed">
          Study strategy, MMI dissections, admissions reality checks, written for the student
          nobody warned that this was going to be this hard.
        </p>
        <div className="mt-10 max-w-3xl">
          <NewsletterSignup variant="block" testid="blog-newsletter" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-loading">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-paper-100 border border-paper-200 rounded-sm h-[420px] animate-pulse" />
            ))}
          </div>
        )}
        {error && !loading && (
          <p className="text-terracotta" data-testid="blog-error">{error}</p>
        )}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-grid">
            {posts.map((p, i) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${i}`}
                className="group flex flex-col bg-paper-100 border border-paper-200 rounded-sm overflow-hidden hover:border-forest transition-colors"
              >
                {p.cover_image && (
                  <div className="aspect-[16/10] overflow-hidden bg-paper-200">
                    <img
                      src={p.cover_image}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 text-xs text-ink-muted">
                    <span className="overline text-terracotta">{p.category}</span>
                    <span className="w-1 h-1 rounded-full bg-ink-muted" />
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {p.read_minutes} min read
                    </span>
                  </div>
                  <h2 className="font-display font-bold text-xl tracking-tight text-ink mt-3 leading-snug group-hover:text-forest transition-colors">
                    {p.title}
                  </h2>
                  <p className="mt-3 text-sm text-ink-muted leading-relaxed flex-1">{p.excerpt}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-forest font-semibold text-sm">
                    Read essay <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
